package com.writerslab.office

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AnalyticsPage() {
    var period by remember { mutableStateOf("day") }
    var data by remember { mutableStateOf<List<OfficeAnalyticsBucket>?>(null) }
    var services by remember { mutableStateOf<List<OfficeServiceBucket>?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun load(p: String) {
        scope.launch {
            error = null
            data = null
            services = null
            if (PREVIEW) {
                data = MockApi.analytics(p)
                services = MockApi.services(p)
                return@launch
            }
            try {
                data = ApiClient.getAnalytics(p)
                services = ApiClient.getServices(p)
            } catch (e: Throwable) {
                error = e.message ?: "Ошибка загрузки"
            }
        }
    }

    LaunchedEffect(period) { load(period) }

    Div({ style { property("padding", "24px 0") } }) {
        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "16px"); marginBottom(20.px) } }) {
            H2({ style { color(Color(C.OnSurface)); property("font-size", "18px"); margin(0.px) } }) {
                Text("Аналитика")
            }
            listOf("day" to "По дням", "week" to "По неделям", "month" to "По месяцам").forEach { (p, label) ->
                Button({
                    style {
                        if (p == period) {
                            property("background", C.Primary)
                            color(Color("#ffffff"))
                        } else {
                            property("background", C.Surface)
                            color(Color(C.Muted))
                        }
                        border(1.px, LineStyle.Solid, Color(C.Border))
                        property("border-radius", "6px")
                        padding(6.px, 14.px)
                        fontSize(13.px)
                        property("cursor", "pointer")
                    }
                    onClick { period = p }
                }) { Text(label) }
            }
            Span({ style { color(Color(C.Muted)); fontSize(12.px) } }) {
                Text("Сутки считаются по UTC")
            }
        }

        if (error != null) {
            P({ style { color(Color(C.Error)) } }) { Text(error!!) }
            return@Div
        }

        val rows = data
        if (rows == null) {
            P({ style { color(Color(C.Muted)) } }) { Text("Загрузка…") }
            return@Div
        }

        if (rows.isEmpty()) {
            P({ style { color(Color(C.Muted)) } }) { Text("Нет данных за выбранный период") }
            // Still render the services table: the two are independent, and
            // a period can easily have service runs but no registrations,
            // uploads or payments — which used to hide it entirely.
            ServicesTable(services)
            return@Div
        }

        val maxRegs = rows.maxOf { it.registrations }.coerceAtLeast(1)
        val maxBooks = rows.maxOf { it.books ?: 0 }.coerceAtLeast(1)
        val maxPayments = rows.maxOf { it.creditsViaPayment }.coerceAtLeast(1)
        val maxCodes = rows.maxOf { it.codeRedemptions }.coerceAtLeast(1)

        Div({ style { property("overflow-x", "auto") } }) {
            Table({
                style {
                    tableStyle()
                    property("min-width", "700px")
                }
            }) {
                Thead {
                    Tr {
                        // "Из них" in the two visit sub-columns is load-bearing:
                        // without it the three read as unrelated numbers, and a
                        // reader has no way to know that new and signed-in
                        // visitors are subsets of the first column rather than
                        // separate populations that could be added up.
                        listOf(
                            "Дата" to null,
                            "Посетители" to
                                "Разные браузеры за сутки. Один человек с телефона и ноутбука — двое.",
                            "Из них новые" to
                                "Пришли к нам впервые, а не просто впервые за этот день.",
                            "Из них с аккаунтом" to
                                "Считаются по аккаунту, поэтому один человек с двух устройств — один.",
                            "Регистрации" to null,
                            "Книги" to
                                "Разные книги, а не число разборов: одну книгу могли разобрать несколько раз.",
                            "Оплаты (кредитов)" to null,
                            "Активации кодов" to null,
                        )
                            .forEach { (h, tip) ->
                                Th({
                                    if (tip != null) attr("title", tip)
                                    style {
                                        property("text-align", if (h == "Дата") "left" else "right")
                                        padding(8.px, 12.px)
                                        property("border-bottom", "2px solid ${C.Border}")
                                        // The three visit headers are long enough to
                                        // push the last column off-screen if kept on
                                        // one line; let them wrap instead of forcing
                                        // a horizontal scroll onto the whole table.
                                        property("white-space", if (tip != null) "normal" else "nowrap")
                                        color(Color(C.Muted))
                                        property("font-weight", "600")
                                        if (tip != null) {
                                            property("max-width", "90px")
                                            // Dotted underline + help cursor: the only
                                            // affordance a native title tooltip has.
                                            property("cursor", "help")
                                            property("text-decoration", "underline dotted")
                                            property("text-underline-offset", "3px")
                                        }
                                    }
                                }) { Text(h) }
                            }
                    }
                }
                Tbody {
                    rows.forEach { r ->
                        Tr({
                            style {
                                property("border-bottom", "1px solid ${C.Border}")
                                property("transition", "background 0.1s")
                            }
                        }) {
                            Td({
                                style {
                                    padding(8.px, 12.px)
                                    property("font-family", "monospace")
                                    property("white-space", "nowrap")
                                }
                            }) { Text(r.date) }
                            // Uniques share no meaningful bar scale with
                            // registrations, so they render as plain numbers;
                            // a dash when the period is not daily.
                            NumCell(r.visitors)
                            NumCell(r.newVisitors)
                            NumCell(r.loggedInVisitors)
                            BarCell(r.registrations, maxRegs, C.Primary)
                            BarCell(r.books ?: 0, maxBooks, "#8b5cf6")
                            BarCell(r.creditsViaPayment, maxPayments, C.Success, suffix = " кр")
                            BarCell(r.codeRedemptions, maxCodes, "#ec4899")
                        }
                    }
                    // Totals row
                    Tr {
                        listOf(
                            "Итого" to null,
                            // NOT sumOf: a visitor present on five days is five
                            // daily uniques but one person. The only honest
                            // single number here is the daily average.
                            dailyAverage(rows.map { it.visitors }) to null,
                            dailyAverage(rows.map { it.newVisitors }) to null,
                            dailyAverage(rows.map { it.loggedInVisitors }) to null,
                            rows.sumOf { it.registrations }.toString() to C.Primary,
                            // NOT a sum: one book analysed on five days is
                            // five daily entries but one book. The backend
                            // sends the true period-wide distinct.
                            (rows.firstNotNullOfOrNull { it.booksTotal }?.toString() ?: "—") to null,
                            "${rows.sumOf { it.creditsViaPayment }} кр" to C.Success,
                            rows.sumOf { it.codeRedemptions }.toString() to null,
                        ).forEach { (text, color) ->
                            Td({
                                style {
                                    padding(8.px, 12.px)
                                    property("text-align", "right")
                                    property("font-weight", "700")
                                    property("border-top", "2px solid ${C.Border}")
                                    if (color != null) color(Color(color))
                                    else color(Color(C.OnSurface))
                                    if (text == "Итого") property("text-align", "left")
                                }
                            }) { Text(text) }
                        }
                    }
                }
            }
        }

        ServicesTable(services)
    }
}

/** Order and labels of the office columns. COVER_BATCH and COVER_REGEN
 *  share one column: they are separate rows in the database but the same
 *  thing to anyone reading the table. */
private val SERVICE_COLUMNS: List<Pair<String, List<String>>> = listOf(
    "Статический анализ" to listOf("STATIC_ANALYSIS"),
    "Глубокий анализ" to listOf("DEEP_ANALYSIS"),
    "Аннотации" to listOf("ANNOTATION"),
    "Обложки" to listOf("COVER_BATCH", "COVER_REGEN"),
    "Ролики" to listOf("TEASER"),
    "AI-подсветка" to listOf("AI_HIGHLIGHTS"),
)

private val SERVICE_TIPS: Map<String, String> = mapOf(
    "Статический анализ" to
        "Бесплатный разбор при загрузке. Повтор той же книги идёт из кэша и не считается.",
    "Глубокий анализ" to "Платный AI-разбор книги.",
    "Аннотации" to "Генерация аннотации — бесплатные и платные вместе.",
    "Обложки" to "Пакет обложек и перегенерация отдельной — вместе.",
    "Ролики" to "Генерация промо-ролика.",
    "AI-подсветка" to "Платный стилистический разбор текста.",
)

private fun sumOver(map: Map<String, Int>, keys: List<String>): Int =
    keys.sumOf { map[it] ?: 0 }

/**
 * Second table on the page: how many times each service was actually run.
 *
 * Deliberately does NOT share the visit table's "never sum" rule. A run
 * is an event, not a person, so totals here are real sums and the weekly
 * and monthly views work fully.
 */
@Composable
private fun ServicesTable(services: List<OfficeServiceBucket>?) {
    if (services == null) return
    if (services.isEmpty()) {
        P({ style { color(Color(C.Muted)) } }) { Text("Услуги за период не запускались") }
        return
    }

    // Heading and legend on one line: the red −n in the cells is not
    // self-explanatory, and a tooltip would not help — you have to already
    // wonder about it to hover.
    Div({
        style {
            property("display", "flex"); property("align-items", "baseline")
            property("gap", "12px"); property("margin", "32px 0 12px")
        }
    }) {
        H2({ style { color(Color(C.OnSurface)); property("font-size", "18px"); margin(0.px) } }) {
            Text("Услуги")
        }
        Span({ style { color(Color(C.Muted)); fontSize(12.px) } }) {
            Text("запусков за период, ")
            Span({ style { color(Color(C.Error)) } }) { Text("−n") }
            Text(" — сколько из них упало")
        }
    }

    Div({ style { property("overflow-x", "auto") } }) {
        Table({ style { tableStyle(); property("min-width", "700px") } }) {
            Thead {
                Tr {
                    Th({
                        style {
                            property("text-align", "left"); padding(8.px, 12.px)
                            property("border-bottom", "2px solid ${C.Border}")
                            property("white-space", "nowrap")
                            color(Color(C.Muted)); property("font-weight", "600")
                        }
                    }) { Text("Дата") }
                    SERVICE_COLUMNS.forEach { (label, _) ->
                        Th({
                            SERVICE_TIPS[label]?.let { attr("title", it) }
                            style {
                                property("text-align", "right"); padding(8.px, 12.px)
                                property("border-bottom", "2px solid ${C.Border}")
                                property("white-space", "normal")
                                property("max-width", "90px")
                                color(Color(C.Muted)); property("font-weight", "600")
                                property("cursor", "help")
                                property("text-decoration", "underline dotted")
                                property("text-underline-offset", "3px")
                            }
                        }) { Text(label) }
                    }
                }
            }
            Tbody {
                services.forEach { row ->
                    Tr({ style { property("border-bottom", "1px solid ${C.Border}") } }) {
                        Td({
                            style {
                                padding(8.px, 12.px)
                                property("font-family", "monospace")
                                property("white-space", "nowrap")
                            }
                        }) { Text(row.date) }
                        SERVICE_COLUMNS.forEach { (_, keys) ->
                            val runs = sumOver(row.runs, keys)
                            val failed = sumOver(row.failed, keys)
                            ServiceCell(runs, failed)
                        }
                    }
                }

                // Totals — a real sum here, unlike the visit table above.
                Tr {
                    TotalCell("Итого", left = true)
                    SERVICE_COLUMNS.forEach { (_, keys) ->
                        TotalCell(services.sumOf { sumOver(it.runs, keys) }.toString())
                    }
                }
                // The reason the status column exists.
                Tr {
                    TotalCell("Провалов", left = true)
                    SERVICE_COLUMNS.forEach { (_, keys) ->
                        val runs = services.sumOf { sumOver(it.runs, keys) }
                        val failed = services.sumOf { sumOver(it.failed, keys) }
                        TotalCell(
                            if (runs == 0) "—" else "${failed * 100 / runs}%",
                            color = if (failed > 0) C.Error else null,
                        )
                    }
                }
            }
        }
    }
}

/** Run count, with the failures of that bucket called out beside it. */
@Composable
private fun ServiceCell(runs: Int, failed: Int) {
    Td({
        style {
            padding(8.px, 12.px)
            property("text-align", "right")
            property("white-space", "nowrap")
            property("font-weight", if (runs > 0) "600" else "normal")
            color(Color(if (runs > 0) C.OnSurface else C.Muted))
        }
    }) {
        Text(if (runs > 0) runs.toString() else "—")
        if (failed > 0) {
            Span({ style { color(Color(C.Error)); fontSize(12.px); property("margin-left", "6px") } }) {
                Text("−$failed")
            }
        }
    }
}

@Composable
private fun TotalCell(text: String, left: Boolean = false, color: String? = null) {
    Td({
        style {
            padding(8.px, 12.px)
            property("text-align", if (left) "left" else "right")
            property("font-weight", "700")
            property("border-top", "2px solid ${C.Border}")
            color(Color(color ?: C.OnSurface))
        }
    }) { Text(text) }
}

/**
 * Daily uniques cannot be totalled — the same visitor coming back on five
 * days is five daily uniques but one person. An average is the only
 * honest single number for the column.
 */
private fun dailyAverage(values: List<Int?>): String {
    val present = values.filterNotNull()
    return if (present.isEmpty()) "—" else "${present.sum() / present.size} в среднем"
}

/** Plain right-aligned number, or a dash when the period has no such value. */
@Composable
private fun NumCell(value: Int?) {
    Td({
        style {
            padding(8.px, 12.px)
            property("text-align", "right")
            property("white-space", "nowrap")
            property("font-weight", if (value != null && value > 0) "600" else "normal")
            color(Color(if (value != null && value > 0) C.OnSurface else C.Muted))
        }
    }) { Text(value?.toString() ?: "—") }
}

@Composable
private fun BarCell(value: Int, max: Int, barColor: String, suffix: String = "") {
    Td({
        style {
            padding(8.px, 12.px)
            property("text-align", "right")
            property("min-width", "120px")
        }
    }) {
        Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "flex-end"); property("gap", "8px") } }) {
            if (value > 0) {
                Div({
                    style {
                        val pct = (value.toDouble() / max * 80).toInt()
                        width(pct.px)
                        height(6.px)
                        property("border-radius", "3px")
                        property("background", barColor)
                        property("opacity", "0.7")
                        property("flex-shrink", "0")
                    }
                }) {}
            }
            Span({
                style {
                    property("font-weight", if (value > 0) "600" else "normal")
                    color(Color(if (value > 0) C.OnSurface else C.Muted))
                    property("min-width", "30px")
                    property("text-align", "right")
                }
            }) {
                Text(if (value > 0) "$value$suffix" else "—")
            }
        }
    }
}
